//------------------------------------------------------ funciones
import { subscribeTable, createRow, updateRow, deleteRow } from "../supabaseRealtime";
import { cacheStore } from "../cache";
import { realtime } from "../store";
import { RANGO_SUPABASE } from "../../functions/serieEventos";

const TABLA = "eventos";

// -------------------------------------------------- mapeo de fila
/* Portería guarda el furgón —y en ENTREGA/RETIRA también el tractor— como
 * lista («["1542"]»), que llega ya convertida en array. Buscar el array
 * entero contra un id no encontraba nunca nada, y la columna salía vacía. */
const etiquetas = (lista, valor) => {
  const ids = (Array.isArray(valor) ? valor : [valor]).filter(
    (v) => v !== null && v !== undefined && String(v).trim() !== "",
  );
  return ids
    .map((id) => lista?.find((x) => String(x.id) === String(id))?.label || String(id))
    .join(", ");
};

const mapEvento = (ev, ubicacionesMap) => {
  const sucursal = ubicacionesMap[String(ev.sucursal)];
  const persona = realtime.personas?.find((p) => String(p.id) === String(ev.persona));
  const personaRelacionada = realtime.personas?.find((p) => String(p.id) === String(ev.personaRelacionada));
  const mecanico = realtime.mecanicos?.find((p) => String(p.id) === String(ev.mecanico));
  const tractor = realtime.tractores?.find((tr) => String(tr.id) === String(ev.tractor));
  const tractorRelacionado = realtime.tractores?.find((tr) => String(tr.id) === String(ev.tractorRelacionado));
  const furgon = realtime.furgones?.find((fg) => String(fg.id) === String(ev.furgon));
  const vehiculo = realtime.vehiculos?.find((vh) => String(vh.id) === String(ev.vehiculo));
  const proveedor = realtime.proveedores?.find((pv) => String(pv.id) === String(ev.proveedor));

  return {
    ...ev,
    nombreSucursal: sucursal ? sucursal.nombre : "",
    nombrePersona: persona ? persona.nombreCompleto : "",
    nombreMecanico: mecanico ? mecanico.nombreCompleto : "",
    nombrePersonaRelacionada: personaRelacionada ? personaRelacionada.nombreCompleto : "",
    tractorFull: tractor ? tractor.label : etiquetas(realtime.tractores, ev.tractor),
    tractorRelacionadoFull: tractorRelacionado ? tractorRelacionado.label : "",
    furgonFull: furgon ? furgon.label : etiquetas(realtime.furgones, ev.furgon),
    vehiculoFull: vehiculo ? vehiculo.label : "",
    nombreProveedor: proveedor ? proveedor.label : "",
  };
};

// -------------------------------------------------- dos tramos, una lista
/* Las listas piden «los últimos N por `orden_num`». Desde el 21/10 lo que nace
 * en Supabase (el hub y el bot) se numera desde 50.000.000 y lo de CantApp
 * sigue en su serie (portería iba por 25.366): por `orden_num`, cualquier
 * evento de acá queda antes que todos los de CantApp. Con el tope —500 en
 * portería, que carga unos 15 por día desde el hub— en un mes la lista sería
 * sólo de lo de acá y lo de CantApp DESAPARECERÍA (revisión del 21/10).
 *
 * Se piden los dos tramos por separado, cada uno con su tope y su orden
 * (dentro de cada serie `orden_num` sí es cronológico), y se juntan por FECHA.
 * Las de CantApp traen la fecha de Firestore (`{_seconds, _nanoseconds}`), las
 * de acá ISO. Sin tocar el esquema: una columna `instante` indexada sería lo
 * más limpio, y es un cambio que aprueba el dueño. */
const instante = (fecha) => {
  if (fecha && typeof fecha === "object") {
    const s = fecha._seconds ?? fecha.seconds;
    if (typeof s !== "number") return null;
    return s * 1000 + Math.floor(Number(fecha._nanoseconds ?? fecha.nanoseconds ?? 0) / 1e6);
  }
  if (typeof fecha === "string" && fecha.trim()) {
    const t = Date.parse(fecha);
    return Number.isNaN(t) ? null : t;
  }
  return null;
};

/* El más nuevo primero; sin fecha legible, al final; empate, por número. */
const masNuevoPrimero = (a, b) => {
  const ta = instante(a.fecha);
  const tb = instante(b.fecha);
  if (ta !== tb) {
    if (ta === null) return 1;
    if (tb === null) return -1;
    return tb - ta;
  }
  return (Number(b.orden_num) || 0) - (Number(a.orden_num) || 0);
};

/* Junta lo que trajo cada tramo. Cada suscripción recibe por realtime los
 * INSERT de toda la tabla (`subscribeTable` no filtra lo que llega por el
 * canal), así que un evento nuevo puede estar en las dos: va una sola vez. */
export const juntarTramos = (deCantApp, propios, limite) => {
  const vistos = new Set();
  const todos = [];
  for (const ev of [...(propios || []), ...(deCantApp || [])]) {
    if (vistos.has(ev.id)) continue;
    vistos.add(ev.id);
    todos.push(ev);
  }
  return todos.sort(masNuevoPrimero).slice(0, limite);
};

/* Cada suscripción activa guarda acá cómo volver a entregar sus filas crudas
 * (ver `reenriquecerEventos`). */
const entregasActivas = new Set();

const subscribeEnDosTramos = (callback, limite, filtro = null) => {
  const ubicacionesMap = cacheStore.ubicaciones?.map || {};
  let deCantApp = null;
  let propios = null;

  // Hasta que no llegaron los dos tramos no se entrega nada: media lista se
  // ve igual que la lista entera (mismo criterio que `subscribeTable`).
  const entregar = () => {
    if (deCantApp === null || propios === null) return;
    callback(
      juntarTramos(deCantApp, propios, limite).map((ev) => mapEvento(ev, ubicacionesMap)),
    );
  };
  entregasActivas.add(entregar);

  const opciones = (tramo) => ({
    orderBy: "orden_num",
    ascending: false,
    limit: limite,
    filter: (q) => tramo(filtro ? filtro(q) : q),
  });

  // Las 123 filas de portería sin `orden_num` (la reconstrucción de
  // septiembre) van con las de CantApp, como antes: al final.
  const cortarCantApp = subscribeTable(
    TABLA,
    (rows) => {
      deCantApp = rows;
      entregar();
    },
    opciones((q) => q.or(`orden_num.lt.${RANGO_SUPABASE},orden_num.is.null`)),
  );
  const cortarPropios = subscribeTable(
    TABLA,
    (rows) => {
      propios = rows;
      entregar();
    },
    opciones((q) => q.gte("orden_num", RANGO_SUPABASE)),
  );

  return () => {
    entregasActivas.delete(entregar);
    cortarCantApp?.();
    cortarPropios?.();
  };
};

// se llama desde store.js cada vez que personas/tractores/furgones se
// actualizan: después de un F5 los eventos suelen llegar antes que las
// personas, y sin esto la columna «Persona» quedaba vacía (y tractor/furgón
// con el id pelado) hasta el próximo cambio en la tabla eventos.
export const reenriquecerEventos = () => {
  entregasActivas.forEach((entregar) => entregar());
};

// -------------------------------------------------- subscribe general
export const subscribeEventos = (callback, limite = 500) =>
  subscribeEnDosTramos(callback, limite);

// -------------------------------------------------- subscribe por area
export const subscribeEventosByArea = (area, callback, limite = 200) =>
  subscribeEnDosTramos(callback, limite, (q) => q.eq("area", area));

// -------------------------------------------------- filtros locales
const filtrarPorArea = (eventos = [], area) =>
  eventos.filter((ev) => (ev.area || "").toLowerCase() === area.toLowerCase());

export const getEventosAdministracion = (eventos) => filtrarPorArea(eventos, "administracion");
export const getEventosPorteria = (eventos) => filtrarPorArea(eventos, "porteria");
export const getEventosTrafico = (eventos) => filtrarPorArea(eventos, "trafico");
export const getEventosSatelital = (eventos) => filtrarPorArea(eventos, "satelital");

// -------------------------------------------------- CRUD
export const addEvento = ({ id, ...data }) => createRow(TABLA, id, data);
export const updateEvento = (id, data) => updateRow(TABLA, id, data);
export const deleteEvento = (id) => deleteRow(TABLA, id);
